import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Clock, Trash2 } from 'lucide-react';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import TaskHasWorkDialog, { taskHasWorkMinutes } from './TaskHasWorkDialog';
import { NewWorkLogModal } from '../NewWorkLogModal';
import { apiErrorMessage } from '../sales/shared';
import { toast } from '../../store/toastStore';
import { undoableDeleteTicket } from '../../utils/undoableDeletes';
import { mayShowTaskDelete } from '../../utils/taskDeleteVisibility';
import { taskPath } from './taskRowModel';
import type { MenuItemDef } from '../ui/Menu';
import type { Ticket } from '../../types';

/**
 * DQ-178 (D-58, R-03) - the "מחיקה" (and optionally "רישום שעות") item of a task row's ⋯,
 * for the screens that did not have it (milestone page, a customer's tasks tab).
 * Same flow as "כל המשימות": 6-second undo, and the "task has hours - cancel it" dialog.
 * "מחיקה" shows only when the server said the viewer manages the task (can_manage_task).
 */
export function useTaskDeleteAction({ withLogTime = false, onChanged }: { withLogTime?: boolean; onChanged?: () => void } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<{ id: string; title: string } | null>(null);
  const [withWork, setWithWork] = useState<{ id: string; minutes: number } | null>(null);
  const [logging, setLogging] = useState<{ id: string; title: string } | null>(null);

  const items = (tk: { id: string; title: string; can_manage_task?: boolean }): MenuItemDef[] => [
    ...(withLogTime
      ? [{ key: 'log', label: t('tasks.list.logTime'), icon: <Clock size={16} strokeWidth={1.75} />, onSelect: () => setLogging({ id: tk.id, title: tk.title }) }]
      : []),
    ...(mayShowTaskDelete(tk)
      ? [{ key: 'delete', label: t('tasks.list.delete'), icon: <Trash2 size={16} strokeWidth={1.75} />, danger: true, separatorBefore: true, onSelect: () => setDeleting({ id: tk.id, title: tk.title }) } as MenuItemDef]
      : []),
  ];

  const confirmDelete = () => {
    if (!deleting) return;
    const target = deleting;
    setDeleting(null);
    undoableDeleteTicket({
      qc: queryClient, id: target.id, taskType: 'task',
      onCommitted: onChanged, onRestore: onChanged,
      onError: (err: unknown) => {
        const minutes = taskHasWorkMinutes(err);
        if (minutes !== null) setWithWork({ id: target.id, minutes });
        else toast('error', apiErrorMessage(err, t('allTasks.deleteError')));
      },
    });
  };

  const dialogs: ReactNode = (
    <>
      {deleting && (
        <ConfirmDeleteModal
          title={t('allTasks.deleteTitle')}
          itemName={deleting.title}
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
      {withWork && (
        <TaskHasWorkDialog
          minutes={withWork.minutes}
          onCancelTask={() => navigate(`${taskPath(withWork.id)}?action=cancel`)}
          onClose={() => setWithWork(null)}
        />
      )}
      {logging && (
        <NewWorkLogModal
          initialTab="tasks"
          initialItem={{ id: logging.id, title: logging.title } as Ticket}
          onClose={() => setLogging(null)}
          onSuccess={() => { setLogging(null); toast('success', t('quickCreate.workLogCreated')); }}
        />
      )}
    </>
  );

  return { items, dialogs };
}
